import type { FunctionComponent } from "react";
import { useEffect, useState, useCallback, useRef } from "react";
import { StyledOverlayContainer, StyledRightBarFixedContainer } from "./StyledComponents";
import { connect } from "react-redux";
import { toggleQualififcationSideBarAction } from "app/actions/qualificationSideBarActions";
import { selectIsQualificationSideBarOpen, selectQualificationSideBarSelection } from "./QualificationSideBarSelectors";
import type { RootState } from "single-spa/store/types";
import type { QualificationSideBarSelectionType } from "./types";
import QualificationSideBarContext from "./QualificationSidebarContext";

declare const window;

interface IQualificationSideBarProps {
    pageUniqueNumber: string;
    stateConfig: any;
    isQualificationSideBarOpen: boolean;
    qualificationSideBarSelection: QualificationSideBarSelectionType;
    toggleQualificationSideBar: (shouldOpen: boolean) => void;
}
const QualificationSideBar: FunctionComponent<IQualificationSideBarProps> = (props) => {
    const {
        pageUniqueNumber,
        isQualificationSideBarOpen,
        qualificationSideBarSelection,
        stateConfig,
        toggleQualificationSideBar,
    } = props;
    const [pageId, setPageId] = useState<string>(undefined);
    const [isSideBarOpen, setIsSideBarOpen] = useState<boolean>(false);
    const sidebarContainerRef = useRef(null);
    const SideBarComponent = stateConfig?.qualificationSideBarComponent;
    useEffect(() => {
        const isSamePage = pageId === pageUniqueNumber;
        if (!isSamePage) {
            if (isQualificationSideBarOpen) {
                toggleQualificationSideBar(false);
            }
            setPageId(pageUniqueNumber);
        }
        setIsSideBarOpen(isSamePage && isQualificationSideBarOpen && !!SideBarComponent);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [SideBarComponent, isQualificationSideBarOpen, pageId, pageUniqueNumber]);

    const hasParentEltOrOneOfClassList = (currentEl, elToFind, classes) => {
        const regExp = new RegExp("\\b(?:" + classes.join("|") + ")\\b", "gi");
        while (currentEl) {
            if (
                currentEl === elToFind ||
                (typeof currentEl?.className?.match === "function" && currentEl?.className.match(regExp) !== null)
            ) {
                return true;
            }
            currentEl = currentEl.parentElement;
        }
        return false;
    };

    const onGlobalClick = useCallback(
        (ev: any) => {
            if (
                sidebarContainerRef &&
                isSideBarOpen &&
                !hasParentEltOrOneOfClassList(ev?.target, sidebarContainerRef?.current, [
                    "WebsiteListTypeSelector", // used in modal for add to monitor list
                    "qualification-sidebar-toggle", // used in qualification sidebar toggle button and label button and modal for add to monitor list
                ])
            ) {
                toggleQualificationSideBar(false);
            }
        },
        [isSideBarOpen, sidebarContainerRef, toggleQualificationSideBar],
    );

    useEffect(() => {
        if (isSideBarOpen) {
            window.addEventListener("click", onGlobalClick, { capture: true });
        }
        return () => {
            // if it was not added, it wont have any effect :)
            window.removeEventListener("click", onGlobalClick, { capture: true });
        };
    }, [isSideBarOpen, onGlobalClick]);

    return (
        !!SideBarComponent && (
            <StyledOverlayContainer>
                <StyledRightBarFixedContainer
                    isSideBarOpen={isSideBarOpen}
                    ref={sidebarContainerRef}
                    data-automation-qualification-side-bar-closed={!isSideBarOpen ? true : undefined}
                >
                    <QualificationSideBarContext.Provider
                        value={{
                            qualificationSideBarSelection,
                            closeQualificationSideBar: () => {
                                toggleQualificationSideBar(false);
                            },
                        }}
                    >
                        {qualificationSideBarSelection?.data?.domain ? <SideBarComponent /> : null}
                    </QualificationSideBarContext.Provider>
                </StyledRightBarFixedContainer>
            </StyledOverlayContainer>
        )
    );
};

const mapStateToProps = (state: RootState) => {
    const { routing } = state;
    const { pageUniqueNumber, stateConfig } = routing;

    return {
        isQualificationSideBarOpen: selectIsQualificationSideBarOpen(state),
        qualificationSideBarSelection: selectQualificationSideBarSelection(state),
        pageUniqueNumber,
        stateConfig,
    };
};
const mapDispatchToProps = (dispatch) => {
    return {
        toggleQualificationSideBar: (shouldOpen: boolean) => {
            dispatch(toggleQualififcationSideBarAction(shouldOpen));
        },
    };
};
const connected = connect(mapStateToProps, mapDispatchToProps)(QualificationSideBar);

export default connected;
